import {z, type ZodType} from 'zod';

import {
  AGE_VALUES,
  ESTATE_VALUES,
  FINANCIAL_DEPENDENTS_VALUES,
  INSURANCE_VALUES,
  MARITAL_VALUES,
  REAL_ESTATE_OWNERSHIP_VALUES,
  RETIREMENT_VALUES,
} from '../../constants';
import type {FhsStepName} from '../../types';

import {optionalCurrencyAmount, requiredCurrencyAmount} from './currencyAmount';

// Rejects strings made of a single repeated character (e.g. "AAAA", "1111").
// Per spec: a real name can't be one character typed N times.
const notAllIdentical = (value: string) => !/^(.)\1+$/.test(value);

const stepNameSchema = z.object({
  fullName: z
    .string({error: 'Name is required'})
    .trim()
    .min(2, {error: 'Name must be at least 2 characters'})
    .max(100, {error: 'Name must not exceed 100 characters'})
    .refine(notAllIdentical, {
      error: 'Name cannot be a single repeated character',
    }),
});

const stepAgeSchema = z.object({
  age: z.enum(AGE_VALUES, {error: 'Please select an option'}),
});

const stepMaritalStatusSchema = z.object({
  maritalStatus: z.enum(MARITAL_VALUES, {error: 'Please select an option'}),
});

const stepFinancialDependentsSchema = z.object({
  financialDependents: z.enum(FINANCIAL_DEPENDENTS_VALUES, {
    error: 'Please select an option',
  }),
});

const stepRetirementSchema = z.object({
  retirement: z.enum(RETIREMENT_VALUES, {error: 'Please select an option'}),
});

const stepInsuranceSchema = z.object({
  insurance: z
    .array(z.enum(INSURANCE_VALUES), {error: 'Select at least one option'})
    .min(1, {error: 'Select at least one option'}),
});

const stepEstatePlanningSchema = z.object({
  estatePlanning: z
    .array(z.enum(ESTATE_VALUES), {error: 'Select at least one option'})
    .min(1, {error: 'Select at least one option'}),
});

const stepIncomeSchema = z.object({
  annualIncome: requiredCurrencyAmount,
  cashSavings: requiredCurrencyAmount,
});

// Both fields are optional — the user can skip the step entirely. When a value
// IS provided, it still has to be a non-negative number under the ceiling.
const stepInvestmentSchema = z.object({
  nonRetirementInvestments: optionalCurrencyAmount,
  retirementAccounts: optionalCurrencyAmount,
});

// The realEstate value is only required when ownership === 'yes'. We can't
// drop `requiredCurrencyAmount` into the object schema directly because that
// would make the field unconditionally required; instead we gate on ownership
// in superRefine and delegate to the shared helper for the actual currency
// parsing + range check, forwarding its issues to the `realEstateValue` path.
const stepRealEstateSchema = z
  .object({
    realEstateOwnership: z.enum(REAL_ESTATE_OWNERSHIP_VALUES, {
      error: 'Please select an option',
    }),
    realEstateValue: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.realEstateOwnership !== 'yes') return;

    const result = requiredCurrencyAmount.safeParse(data.realEstateValue ?? '');

    if (result.success) return;

    result.error.issues.forEach((issue) => {
      ctx.addIssue({
        code: 'custom',
        path: ['realEstateValue', ...issue.path],
        message: issue.message,
      });
    });
  });

const stepDebtSchema = z.object({
  outstandingDebt: requiredCurrencyAmount,
  // Monthly payments are optional — user can skip when they don't know.
  monthlyDebtPayments: optionalCurrencyAmount,
});

const stepMonthlyExpensesSchema = z.object({
  monthlyExpenses: requiredCurrencyAmount,
});

// Single source of truth used by both real-time validation (UPDATE_FIELD action)
// and the NEXT transition guard. Keep this map in sync as new steps are added.
const SCHEMAS: Partial<Record<FhsStepName, ZodType>> = {
  name: stepNameSchema,
  age: stepAgeSchema,
  maritalStatus: stepMaritalStatusSchema,
  financialDependents: stepFinancialDependentsSchema,
  retirement: stepRetirementSchema,
  insurance: stepInsuranceSchema,
  estatePlanning: stepEstatePlanningSchema,
  income: stepIncomeSchema,
  investment: stepInvestmentSchema,
  realEstate: stepRealEstateSchema,
  debt: stepDebtSchema,
  monthlyExpenses: stepMonthlyExpensesSchema,
};

export const getSchemaForStep = (step: FhsStepName): ZodType | null =>
  SCHEMAS[step] ?? null;
